import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { encounterQueryKeys, encountersApi } from "../../shared/api/encounters";
import type { Vitals } from "../../shared/types/encounter";
import { apiError } from "../../shared/api/client";
import { formatDateTime } from "../../shared/format";
import { EncounterShell } from "./components/EncounterShell";

/**
 * Vitals capture grid. Each cell is large, type-once-and-tab — typical
 * Vietnamese clinic flow is: nurse takes vitals on paper, types them in
 * 30 seconds during patient registration.
 */
export function EncounterVitalsPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/encounters" replace />;

  return (
    <EncounterShell encounterId={id}>
      {(e) => <VitalsForm encounterId={id} vitals={e.vitals} readOnly={e.status !== "IN_PROGRESS"} />}
    </EncounterShell>
  );
}

function VitalsForm({
  encounterId,
  vitals,
  readOnly,
}: {
  encounterId: string;
  vitals: Vitals;
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const [v, setV] = useState<Vitals>(vitals);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setV(vitals), [vitals]);

  const save = useMutation({
    mutationFn: () => encountersApi.updateVitals(encounterId, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(encounterId) });
      setError(null);
    },
    onError: (e) => setError(apiError(e).detail),
  });

  function update<K extends keyof Vitals>(key: K, raw: string) {
    const value = raw === "" ? null : Number(raw);
    setV((prev) => ({ ...prev, [key]: value }) as Vitals);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 border-b p-4 sm:p-6">
        <CardTitle>Sinh hiệu</CardTitle>
        {v.recordedAt && (
          <span className="text-xs text-muted-foreground">
            Cập nhật lúc {formatDateTime(v.recordedAt)}
          </span>
        )}
      </CardHeader>

      <CardContent className="p-4 sm:p-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Cell
            label="Huyết áp"
            unit="mmHg"
            wide
            outOfRange={
              (v.systolicBp != null && (v.systolicBp >= 140 || v.systolicBp < 90)) ||
              (v.diastolicBp != null && (v.diastolicBp >= 90 || v.diastolicBp < 60))
            }
          >
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={50}
                max={250}
                value={v.systolicBp ?? ""}
                onChange={(e) => update("systolicBp", e.target.value)}
                disabled={readOnly}
                aria-label="Huyết áp tâm thu"
                placeholder="—"
                className="text-center text-lg font-semibold tabular-nums"
              />
              <span className="text-lg font-semibold text-muted-foreground">/</span>
              <Input
                type="number"
                min={30}
                max={150}
                value={v.diastolicBp ?? ""}
                onChange={(e) => update("diastolicBp", e.target.value)}
                disabled={readOnly}
                aria-label="Huyết áp tâm trương"
                placeholder="—"
                className="text-center text-lg font-semibold tabular-nums"
              />
            </div>
          </Cell>

          <SimpleCell
            label="Mạch"
            unit="bpm"
            value={v.heartRate}
            onChange={(s) => update("heartRate", s)}
            readOnly={readOnly}
            outOfRange={
              v.heartRate != null && (v.heartRate < 50 || v.heartRate > 110)
            }
          />
          <SimpleCell
            label="Nhiệt độ"
            unit="°C"
            step={0.1}
            value={v.temperatureC}
            onChange={(s) => update("temperatureC", s)}
            readOnly={readOnly}
            outOfRange={
              v.temperatureC != null && (v.temperatureC < 35 || v.temperatureC >= 38)
            }
          />
          <SimpleCell
            label="Nhịp thở"
            unit="lần/phút"
            value={v.respiratoryRate}
            onChange={(s) => update("respiratoryRate", s)}
            readOnly={readOnly}
            outOfRange={
              v.respiratoryRate != null && (v.respiratoryRate < 12 || v.respiratoryRate > 24)
            }
          />
          <SimpleCell
            label="SpO₂"
            unit="%"
            value={v.spO2}
            onChange={(s) => update("spO2", s)}
            readOnly={readOnly}
            outOfRange={v.spO2 != null && v.spO2 < 95}
          />
          <SimpleCell
            label="Cân nặng"
            unit="kg"
            step={0.1}
            value={v.weightKg}
            onChange={(s) => update("weightKg", s)}
            readOnly={readOnly}
          />
          <SimpleCell
            label="Chiều cao"
            unit="cm"
            value={v.heightCm}
            onChange={(s) => update("heightCm", s)}
            readOnly={readOnly}
          />
        </div>

        {error && (
          <div
            role="alert"
            className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        {!readOnly && (
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Đang lưu…" : "Lưu sinh hiệu"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SimpleCell({
  label,
  unit,
  value,
  onChange,
  readOnly,
  step = 1,
  outOfRange,
}: {
  label: string;
  unit: string;
  value: number | null;
  onChange: (raw: string) => void;
  readOnly?: boolean;
  step?: number;
  outOfRange?: boolean;
}) {
  return (
    <Cell label={label} unit={unit} outOfRange={outOfRange}>
      <Input
        type="number"
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        placeholder="—"
        aria-label={`${label} (${unit})`}
        className="text-center text-lg font-semibold tabular-nums"
      />
    </Cell>
  );
}

function Cell({
  label,
  unit,
  children,
  wide,
  outOfRange,
}: {
  label: string;
  unit: string;
  children: React.ReactNode;
  wide?: boolean;
  outOfRange?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 rounded-md border bg-background p-3",
        wide && "col-span-2",
        outOfRange && "border-destructive/50 bg-destructive/5"
      )}
    >
      <Label className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {outOfRange && (
          <AlertTriangle className="size-3.5 text-destructive" aria-hidden="true" />
        )}
      </Label>
      {children}
      <span className="text-xs text-muted-foreground">{unit}</span>
    </div>
  );
}
